import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  adminDataApi,
  type ProgramDto,
  type ProgramInput
} from "../../services/admin-data-api";
import { useAuthStore } from "../../store/auth-store";

const statusColor: Record<string, "default" | "success" | "warning" | "error"> = {
  ACTIVE: "success",
  INACTIVE: "warning",
  ARCHIVED: "default"
};

const emptyForm: ProgramInput = {
  name: "",
  description: "",
  status: "ACTIVE"
};

export const ProgramsPage = () => {
  const [programs, setPrograms] = useState<ProgramDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<ProgramDto | null>(null);
  const [form, setForm] = useState<ProgramInput>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const session = useAuthStore((state) => state.session);
  const canManage = session?.user.role === "ADMIN";

  const loadPrograms = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminDataApi.getPrograms();
      setPrograms(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load programs");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPrograms().catch(() => {
      setError("Unable to load programs");
      setIsLoading(false);
    });
  }, []);

  const handleOpenCreate = () => {
    setEditingProgram(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (program: ProgramDto) => {
    setEditingProgram(program);
    setForm({
      name: program.name,
      description: program.description ?? "",
      status: program.status
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProgram(null);
    setForm(emptyForm);
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      if (editingProgram) {
        await adminDataApi.updateProgram(editingProgram.id, form);
      } else {
        await adminDataApi.createProgram(form);
      }
      await loadPrograms();
      handleCloseDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save program");
    } finally {
      setIsSaving(false);
    }
  };

  const sortedPrograms = useMemo(
    () =>
      [...programs].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      ),
    [programs]
  );

  return (
    <>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Grid item>
          <Typography variant="h4" component="h1">
            Programs
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage benefit programs and their availability.
          </Typography>
        </Grid>
        <Grid item>
          {canManage ? (
            <Button variant="contained" onClick={handleOpenCreate}>
              New Program
            </Button>
          ) : null}
        </Grid>
      </Grid>

      {isLoading ? <CircularProgress sx={{ display: "block", my: 4 }} /> : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      <Stack spacing={2}>
        {sortedPrograms.map((program) => (
          <Box
            key={program.id}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              p: 2,
              cursor: canManage ? "pointer" : "default",
              "&:hover": canManage
                ? { borderColor: "primary.light", boxShadow: 1 }
                : undefined
            }}
            onClick={canManage ? () => handleOpenEdit(program) : undefined}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6">{program.name}</Typography>
                {program.description ? (
                  <Typography variant="body2" color="text.secondary">
                    {program.description}
                  </Typography>
                ) : null}
              </Box>
              <Chip
                color={statusColor[program.status] ?? "default"}
                label={program.status.toLowerCase()}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Updated {new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(program.updatedAt))}
            </Typography>
          </Box>
        ))}
        {!isLoading && sortedPrograms.length === 0 ? (
          <Alert severity="info">No programs defined yet.</Alert>
        ) : null}
      </Stack>

      <Dialog open={dialogOpen && canManage} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingProgram ? "Edit Program" : "Create Program"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Program Name"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              required
            />
            <TextField
              label="Description"
              value={form.description ?? ""}
              multiline
              minRows={3}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            />
            <TextField
              select
              SelectProps={{ native: true }}
              label="Status"
              value={form.status ?? "ACTIVE"}
              onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="ARCHIVED">Archived</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
